import { useSyncExternalStore } from 'react';

/*
 * Whether the person owns the lifetime "no ads" unlock. The store (Google Play / App Store) is the source of
 * truth and tells the app on every start (see `lifetime.ts`); this flag only caches the answer so the first
 * screen already knows. It lives outside redux so any part of the app, including the translation layer and
 * plain modules, can read it without a provider.
 */

const KEY = 'arcana:lifetime';

const read = (): boolean => {
    try {
        return localStorage.getItem(KEY) === '1';
    } catch {
        return false;
    }
};

let owned = read();
const listeners = new Set<() => void>();

export const isAdFree = (): boolean => owned;

export const setAdFree = (value: boolean): void => {
    if (value === owned) return;
    owned = value;
    try {
        if (value) localStorage.setItem(KEY, '1');
        else localStorage.removeItem(KEY);
    } catch {
        // Storage blocked: the store reports ownership again on the next start.
    }
    listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/** True once the lifetime unlock is owned. Re-renders when it changes. */
export const useAdFree = (): boolean =>
    useSyncExternalStore(subscribe, isAdFree, () => false);
